import { EventType } from "@/analytics/analytics-events";
import {
  DeleteWaypointProperties,
  type DeleteTimePointProperties,
} from "@/analytics/data-organization-events-properties";
import { DeleteElementDialogContent } from "@/components/ui/delete-element-dialog-content";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectAllSheets, selectSheetForElement } from "@/modes/mode-selectors";
import { selectBackgroundTaskForIElement } from "@/store/background-tasks/background-tasks-selector";
import { removeIElementFromStore } from "@/store/ielements-store-utilities";
import { changeMode } from "@/store/mode-slice";
import { selectAreaSections } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { isLocationMarker } from "@/types/ielement-extra-types";
import {
  DeleteIcon,
  FaroMenuItem,
  FaroText,
  FaroTooltip,
  TranslateVar,
  useDialog,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, convertToDateString } from "@faro-lotv/foundation";
import {
  IElement,
  isIElementAreaSection,
  isIElementBimModelSection,
  isIElementGenericImgSheet,
  isIElementImg2d,
  isIElementImg360,
  isIElementOverviewImage,
  isIElementRoomSection,
  isIElementSectionDataSession,
  isIElementTimeSeriesRoom,
} from "@faro-lotv/ielement-types";
import {
  isIElementVideoModeCopy,
  selectAncestor,
  selectChildrenDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import {
  createMutationDeleteElement,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, useMemo, type JSX } from "react";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";
import { isElementInUse } from "../utils";

type DeleteActionProps = {
  /** The ID of the element to delete */
  elementId: string;
  /** Enable the dark version of the menu */
  dark?: boolean;
  /** Callback when the menu should be closed */
  closeContextMenu?(): void;
  /** Optional callback to handle custom deletion logic */
  handleElementDeletion?(element: IElement): boolean;
};

/**
 * @returns A context menu action to delete an element
 */
export function DeleteAction({
  elementId,
  dark,
  closeContextMenu,
  handleElementDeletion,
}: DeleteActionProps): JSX.Element {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const { createDialog } = useDialog();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();

  const el = selectIElement(elementId)(store.getState());
  assert(
    el,
    "Delete action is not supposed to be called for an undefined element",
  );

  const deleteElement = useCallback(async () => {
    closeContextMenu?.();

    trackElementDeletion(el, store.getState());

    assert(
      !el.parentId || !isIElementBimModelSection(el),
      "Delete action is not supposed to be called for a 3D Model element",
    );

    const deleteMutation = createMutationDeleteElement(elementId, true);

    const { title, description } = selectDeletionDialogContent(el)(
      store.getState(),
    );

    // Create a delete confirmation dialog
    const hasConfirmed = await createDialog({
      title,
      confirmText: "Delete",
      content: <DeleteElementDialogContent name={description} />,
      variant: "danger",
    });

    if (!hasConfirmed) return;

    try {
      // Delete the element from the project.
      await projectApiClient.applyMutations([deleteMutation]);

      // Manage the deletion of an element that is currently active
      if (isElementInUse(el, store.getState())) {
        // What to do after the deletion of an element in use can be manually handled if the callback is provided
        // If the callback does not return true, we fallback to the simple default handling
        const isHandled = handleElementDeletion?.(el);
        if (!isHandled) {
          // Prevent crash when removing the element from overview mode.
          // TODO: create generic way of exiting mode when require data for the mode is removed. https://faro01.atlassian.net/browse/SWEB-1334
          const sheet = selectSheetForElement(el)(store.getState());

          // Check if the current element is a parent of the current sheet
          const parentElement = selectAncestor(
            sheet,
            (ancestor) => el.id === ancestor.id,
          )(store.getState());
          // If the current element is an area, change the active area
          if (parentElement && isIElementAreaSection(parentElement)) {
            const areas = selectAreaSections(store.getState());
            const activeArea = areas.find((a) => a.id !== parentElement.id);
            dispatch(setActiveElement(activeArea?.id));
            dispatch(changeMode("start"));
          } else if (sheet) {
            dispatch(setActiveElement(sheet.id));
            dispatch(changeMode("sheet"));
          } else {
            dispatch(changeMode("createArea"));
          }
        }
      }

      // TODO: Delete the time series containing the point cloud if it has not any remaining child
      // Ticket: https://faro01.atlassian.net/browse/SWEB-1737
      // Update store after removing the element.
      removeIElementFromStore(elementId, dispatch);
    } catch (error) {
      handleErrorWithToast({
        title: `Failed to remove "${el.name}"`,
        error,
      });
    } finally {
      closeContextMenu?.();
    }
  }, [
    closeContextMenu,
    createDialog,
    dispatch,
    el,
    elementId,
    handleElementDeletion,
    handleErrorWithToast,
    projectApiClient,
    store,
  ]);

  const parentArea = selectAncestor(
    el,
    isIElementAreaSection,
  )(store.getState());
  const sheets = selectAllSheets(parentArea)(store.getState());
  const tasks = selectBackgroundTaskForIElement(el.id)(store.getState());

  const disabledMessage = useMemo(() => {
    if (isIElementOverviewImage(el)) {
      return "Overview map can't be deleted";
    }

    if (isIElementGenericImgSheet(el)) {
      if (!parentArea) {
        return "Area doesn't have any sheet to remove";
      }

      if (sheets.length <= 1) {
        return "At least one sheet must remain";
      }
    }

    if (isIElementAreaSection(el) && tasks) {
      return "Area can't be deleted while a background task is running";
    }
  }, [el, parentArea, sheets.length, tasks]);

  return (
    <FaroTooltip title={disabledMessage} placement="right">
      <FaroMenuItem
        label="Delete"
        aria-label="Delete"
        dark={dark}
        Icon={DeleteIcon}
        onClick={deleteElement}
        disabled={!!disabledMessage}
      />
    </FaroTooltip>
  );
}

export const DELETE_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.delete,
  CustomComponent: DeleteAction,
};

function trackElementDeletion(
  elementToDelete: IElement,
  state: RootState,
): void {
  const parentElement = selectIElement(elementToDelete.parentId)(state);

  const isVideoModeCopy =
    selectChildrenDepthFirst(elementToDelete, isIElementVideoModeCopy)(state)
      .length > 0;

  if (isIElementSectionDataSession(elementToDelete)) {
    Analytics.track(EventType.deletePointCloud);
  } else if (isIElementAreaSection(elementToDelete)) {
    Analytics.track(EventType.deleteArea);
  } else if (isIElementGenericImgSheet(elementToDelete)) {
    Analytics.track(EventType.deleteSheet);
  } else if (parentElement && isIElementTimeSeriesRoom(parentElement)) {
    Analytics.track<DeleteTimePointProperties>(EventType.deleteTimePoint, {
      isVideoModeCopy,
    });
  } else if (isLocationMarker(elementToDelete, state)) {
    Analytics.track(EventType.deleteLocationMarker);
  } else if (isIElementRoomSection(elementToDelete)) {
    Analytics.track<DeleteWaypointProperties>(EventType.deleteWaypoint, {
      num360Photos: selectChildrenDepthFirst(
        elementToDelete,
        isIElementImg360,
      )(state).length,
      num2dPhotos: selectChildrenDepthFirst(
        elementToDelete,
        isIElementImg2d,
      )(state).length,
      isVideoModeCopy,
    });
  }
}

function selectDeletionDialogContent(elementToDelete: IElement) {
  return (state: RootState) => {
    const parentElement = selectIElement(elementToDelete.parentId)(state);
    const videoModeCopies = selectChildrenDepthFirst(
      elementToDelete,
      isIElementVideoModeCopy,
    )(state);

    // Defaults applicable for any element
    const title = "Delete element?";
    const description = (
      <FaroText variant="heading14">
        <TranslateVar name="elementName">{elementToDelete.name}</TranslateVar>
      </FaroText>
    );

    if (parentElement && isIElementTimeSeriesRoom(parentElement)) {
      // Time points are only referenced by their date in the Viewer
      if (videoModeCopies.length > 0) {
        return {
          title: "Delete time point?",
          description: (
            <>
              the time point at{" "}
              <TranslateVar name="timePointDate">
                {convertToDateString(elementToDelete.createdAt)}
              </TranslateVar>
              . This time point has VideoMode captures associated with it. If
              you delete this time point, the VideoMode captures will also be
              deleted
            </>
          ),
        };
      }
      return {
        title: "Delete time point?",
        description: (
          <>
            the time point at{" "}
            <TranslateVar name="timePointDate">
              {convertToDateString(elementToDelete.createdAt)}
            </TranslateVar>
          </>
        ),
      };
    } else if (isLocationMarker(elementToDelete, state)) {
      return {
        title: "Delete location marker?",
        description,
      };
    } else if (isIElementRoomSection(elementToDelete)) {
      if (videoModeCopies.length > 0) {
        return {
          title: "Delete waypoint?",
          description: (
            <>
              {description}. This waypoint has VideoMode captures associated
              with it. If you delete this waypoint, the VideoMode captures will
              also be deleted
            </>
          ),
        };
      }
      return {
        title: "Delete waypoint?",
        description,
      };
    }

    return {
      title,
      description,
    };
  };
}
